// ============================================================================
// GESTIMMONY — Assistant IA (Claude Sonnet 5)
// ============================================================================
// Chat streaming avec un expert virtuel en droit locatif + fiscalité.
// - Streaming SSE via fetch + ReadableStream (pas EventSource car POST + cookies)
// - Sessions persistantes (sidebar)
// - Suggestions de départ pour un onboarding zéro friction
// ============================================================================
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { planContext } from "@/lib/plan";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Sparkles, Send, Trash2, Plus, MessageSquare, Loader2, Lock } from "lucide-react";

const SUGGESTIONS = [
  { title: "Calcul IRL", q: "Peux-tu m'expliquer comment calculer précisément la révision annuelle de loyer avec l'IRL, avec un exemple chiffré ?" },
  { title: "Impayé récent", q: "Mon locataire n'a pas payé son loyer ce mois-ci. Quelles sont mes options légales étape par étape ?" },
  { title: "Régime fiscal LMNP", q: "Je loue un studio meublé, dois-je choisir le régime micro-BIC ou réel ? Quels sont les seuils et avantages ?" },
  { title: "DPE obligatoire", q: "Quelles sont les échéances légales des diagnostics obligatoires (DPE, gaz, électricité) pour une location vide ?" },
  { title: "Congé pour vente", q: "Comment donner correctement un congé pour vente à mon locataire ? Quels délais et formalisme ?" },
  { title: "Charges récupérables", q: "Peux-tu me lister les principales charges récupérables auprès du locataire selon le décret de 1987 ?" },
];

export default function AssistantPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pendingReply, setPendingReply] = useState("");
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/assistant/sessions");
      setSessions(data);
    } catch { /* silent */ }
  };

  const loadSession = async (sid) => {
    setSessionId(sid);
    try {
      const { data } = await api.get(`/assistant/sessions/${sid}/messages`);
      setMessages(data);
    } catch (e) { toast.error("Erreur chargement session"); }
  };

  const newSession = () => {
    setSessionId(null);
    setMessages([]);
    setPendingReply("");
    setInput("");
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette conversation ?")) return;
    try {
      await api.delete(`/assistant/sessions/${sid}`);
      if (sessionId === sid) newSession();
      loadSessions();
    } catch { toast.error("Erreur suppression"); }
  };

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => {
    // scroll to bottom on new content
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pendingReply]);

  const send = async (overrideMessage) => {
    const text = (overrideMessage ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setPendingReply("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);

    try {
      const controller = new AbortController();
      abortRef.current = controller;
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/assistant/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullAnswer = "";
      let newSessionId = sessionId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.delta) {
              fullAnswer += evt.delta;
              setPendingReply(fullAnswer);
            } else if (evt.done) {
              newSessionId = evt.session_id || sessionId;
            } else if (evt.error) {
              throw new Error(evt.error);
            }
          } catch { /* ignore malformed */ }
        }
      }

      setMessages((prev) => [...prev, { role: "assistant", content: fullAnswer }]);
      setPendingReply("");
      if (newSessionId && newSessionId !== sessionId) {
        setSessionId(newSessionId);
      }
      loadSessions();
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Erreur assistant : " + e.message);
      setPendingReply("");
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const stopStream = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const { hasAI, canWrite, limits } = planContext(user);
  if (!hasAI) return <AiLocked readOnly={!canWrite} planLabel={limits.label} />;

  const showEmpty = messages.length === 0 && !pendingReply;

  return (
    <div className="grid grid-cols-12 gap-4 h-[calc(100vh-8rem)]" data-testid="assistant-page">
      {/* Sidebar : sessions */}
      <aside className="col-span-12 md:col-span-3 flex flex-col gap-3">
        <Button
          onClick={newSession}
          data-testid="assistant-new-session"
          className="w-full bg-[hsl(var(--primary))]"
        >
          <Plus className="h-4 w-4 mr-2" /> Nouvelle conversation
        </Button>
        <Card className="flex-1 p-2 overflow-hidden">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 px-2 py-1">
            Historique
          </div>
          <ScrollArea className="h-full pr-2">
            {sessions.length === 0 && (
              <div className="text-xs text-slate-400 px-2 py-4 text-center">Aucune conversation</div>
            )}
            {sessions.map((s) => (
              <div
                key={s.session_id}
                data-testid={`session-${s.session_id}`}
                onClick={() => loadSession(s.session_id)}
                className={`group flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-slate-100 ${
                  sessionId === s.session_id ? "bg-teal-50 border border-teal-200" : ""
                }`}
              >
                <MessageSquare className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-700 truncate">{s.last_message || "Session vide"}</div>
                  <div className="text-[10px] text-slate-400">{s.count} messages</div>
                </div>
                <button
                  onClick={(e) => deleteSession(s.session_id, e)}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </ScrollArea>
        </Card>
      </aside>

      {/* Chat */}
      <section className="col-span-12 md:col-span-9 flex flex-col">
        <Card className="flex-1 flex flex-col overflow-hidden">
          <div className="border-b border-slate-200 px-5 py-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-slate-900">Assistant Gestimmony</div>
              <div className="text-xs text-slate-500">Expert droit locatif & fiscalité — propulsé par Claude Sonnet 5</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="assistant-messages">
            {showEmpty && (
              <div className="max-w-2xl mx-auto text-center py-10">
                <div className="h-14 w-14 mx-auto rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center mb-4">
                  <Sparkles className="h-7 w-7 text-white" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-slate-900">Comment puis-je vous aider ?</h2>
                <p className="mt-2 text-sm text-slate-500">
                  Posez une question sur vos baux, vos impayés, l'IRL, votre déclaration 2044 ou n'importe quel litige.
                </p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`suggestion-${i}`}
                      onClick={() => send(s.q)}
                      className="text-left border border-slate-200 rounded-xl p-3 hover:border-teal-500 hover:bg-teal-50/40 transition text-sm"
                    >
                      <div className="font-semibold text-slate-900 text-xs mb-0.5">{s.title}</div>
                      <div className="text-slate-500 text-xs line-clamp-2">{s.q}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <MessageBubble key={i} role={m.role} content={m.content} />
            ))}
            {pendingReply && <MessageBubble role="assistant" content={pendingReply} streaming />}
            {streaming && !pendingReply && (
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> L'assistant réfléchit…
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 p-3">
            <div className="flex items-end gap-2">
              <Textarea
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Posez votre question… (Entrée pour envoyer, Maj+Entrée pour un saut de ligne)"
                disabled={streaming}
                rows={2}
                className="resize-none"
              />
              {streaming ? (
                <Button data-testid="assistant-stop" onClick={stopStream} variant="outline">
                  Stopper
                </Button>
              ) : (
                <Button
                  data-testid="assistant-send"
                  onClick={() => send()}
                  disabled={!input.trim()}
                  className="bg-[hsl(var(--primary))]"
                >
                  <Send className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="mt-1.5 text-[10px] text-slate-400 text-center">
              L'assistant peut se tromper. Vérifiez les informations juridiques critiques auprès d'un professionnel.
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
}

function AiLocked({ readOnly, planLabel }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="assistant-locked">
      <Card className="max-w-md w-full p-8 text-center">
        <div className="h-14 w-14 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
          <Lock className="h-7 w-7 text-slate-500" />
        </div>
        <h2 className="font-heading text-xl font-bold text-slate-900">
          {readOnly ? "Assistant IA indisponible" : "Assistant IA non inclus"}
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          {readOnly
            ? "Votre abonnement n'est pas actif : vous êtes en mode consultation. Réactivez une formule Patrimoine ou Prestige pour utiliser l'Assistant IA."
            : `L'Assistant IA est inclus dans les formules Patrimoine et Prestige. Votre formule ${planLabel} n'y donne pas accès.`}
        </p>
        <Link to="/app/abonnement">
          <Button className="mt-6 bg-[hsl(var(--primary))]" data-testid="assistant-locked-cta">
            Voir les offres
          </Button>
        </Link>
      </Card>
    </div>
  );
}

function MessageBubble({ role, content, streaming }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shrink-0">
          <Sparkles className="h-4 w-4 text-white" />
        </div>
      )}
      <div
        data-testid={`message-${role}`}
        className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
          isUser
            ? "bg-[hsl(var(--primary))] text-white"
            : "bg-slate-100 text-slate-900"
        }`}
      >
        {formatContent(content)}
        {streaming && <span className="inline-block ml-1 animate-pulse">▊</span>}
      </div>
      {isUser && (
        <div className="h-8 w-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-xs font-bold shrink-0">
          MOI
        </div>
      )}
    </div>
  );
}

// Rendu markdown light : **gras**, listes, liens
function formatContent(text) {
  if (!text) return null;
  // gras entre **...**
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}
